import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SearchInput } from "@/components/SearchInput";
import { PullToRefresh } from "@/components/PullToRefresh";
import { PinnedSearches } from "@/components/PinnedSearches";
import { SaveButton } from "@/components/SaveButton";
import { NewBadge } from "@/components/NewBadge";
import { ListingSkeleton } from "@/components/skeletons";
import { Search, Plus, HandCoins, Car, Square, LayoutGrid } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { useResponsive } from "@/hooks/useResponsive";
import { isListingNew, formatPublishedAt } from "@/utils/listingHelpers";
import { getMediumImageUrl } from "@/utils/imageOptimization";

interface Listing {
  id: string;
  title: string;
  price: number;
  images: string[];
  created_at: string;
  user_id: string;
}

interface ExtendedListing extends Listing {
  isSaved?: boolean;
}
const Index = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { isMobile: isMobileResponsive, isTablet } = useResponsive();
  const showPullToRefresh = isMobileResponsive || isTablet;
  
  const [mobileColumns, setMobileColumns] = useState<1 | 2>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('homepage-mobile-columns');
      return saved === '2' ? 2 : 1;
    }
    return 1;
  });
  const [user, setUser] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [recentListings, setRecentListings] = useState<ExtendedListing[]>([]);
  const [isLoadingListings, setIsLoadingListings] = useState(true);
  const [feedMode, setFeedMode] = useState<"latest" | "personalized">("latest");
  const [hasPersonalizationData, setHasPersonalizationData] = useState(false);
  useEffect(() => {
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setUser(session?.user ?? null);
    });
    
    return () => subscription.unsubscribe();
  }, []);

  // Save mobile columns preference to localStorage
  useEffect(() => {
    localStorage.setItem('homepage-mobile-columns', mobileColumns.toString());
  }, [mobileColumns]);

  useEffect(() => {
    // Check if user has personalization data
    if (user) {
      checkPersonalizationData();
    } else {
      setHasPersonalizationData(false);
    }
  }, [user]);

  useEffect(() => {
    if (feedMode === "personalized" && user && hasPersonalizationData) {
      fetchPersonalizedListings();
    } else {
      fetchRecentListings();
    }
  }, [feedMode, user, hasPersonalizationData]);

  const checkPersonalizationData = async () => {
    if (!user) return;
    
    const [savedSearches, recentSearches, savedListings] = await Promise.all([
      supabase.from('saved_searches').select('id').eq('user_id', user.id).limit(1),
      supabase.from('recent_searches').select('id').eq('user_id', user.id).limit(1),
      supabase.from('saved_listings').select('id').eq('user_id', user.id).limit(1)
    ]);
    
    const hasData = 
      (savedSearches.data && savedSearches.data.length > 0) ||
      (recentSearches.data && recentSearches.data.length > 0) ||
      (savedListings.data && savedListings.data.length > 0);
    
    setHasPersonalizationData(hasData);
  };
  
  const fetchPersonalizedListings = async () => {
    if (!user) return;
    
    setIsLoadingListings(true);
    
    try {
      // Fetch user's saved searches
      const { data: savedSearchesData } = await supabase
        .from('saved_searches')
        .select('search_term')
        .eq('user_id', user.id);
      
      // Fetch user's recent searches
      const { data: recentSearchesData } = await supabase
        .from('recent_searches')
        .select('search_term')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(10);
      
      // Fetch user's saved listings to understand preferences
      const { data: savedListingsData } = await supabase
        .from('saved_listings')
        .select('listing_id')
        .eq('user_id', user.id)
        .limit(20);
      
      let interestTerms: string[] = [];
      
      // If user has saved listings, fetch their details to extract brands/models
      if (savedListingsData && savedListingsData.length > 0) {
        const listingIds = savedListingsData.map(sl => sl.listing_id);
        const { data: listingsData } = await supabase
          .from('listings')
          .select('car_brand, car_model')
          .in('id', listingIds);
        
        if (listingsData) {
          listingsData.forEach(listing => {
            if (listing.car_brand) interestTerms.push(listing.car_brand.toLowerCase());
            if (listing.car_model) interestTerms.push(listing.car_model.toLowerCase());
          });
        }
      }
      
      // Combine all search terms
      const allSearchTerms = [
        ...(savedSearchesData?.map(s => s.search_term.toLowerCase()) || []),
        ...(recentSearchesData?.map(s => s.search_term.toLowerCase()) || []),
        ...interestTerms
      ];
      
      // Remove duplicates
      const uniqueTerms = Array.from(new Set(allSearchTerms));
      
      if (uniqueTerms.length > 0) {
        // Fetch more listings to filter
        const { data, error } = await supabase
          .from('listings')
          .select('id, title, price, images, created_at, user_id, description, car_brand, car_model')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(100); // Fetch more to have better filtering options
        
        if (data && !error) {
          // Smart filtering: score each listing based on matches
          const scoredListings = data.map(listing => {
            const searchableText = `${listing.title} ${listing.description} ${listing.car_brand} ${listing.car_model}`.toLowerCase();
            let score = 0;
            
            uniqueTerms.forEach(term => {
              if (searchableText.includes(term)) {
                // Higher score for title/brand/model matches
                if (listing.title.toLowerCase().includes(term)) score += 5;
                if (listing.car_brand?.toLowerCase().includes(term)) score += 4;
                if (listing.car_model?.toLowerCase().includes(term)) score += 4;
                if (listing.description?.toLowerCase().includes(term)) score += 2;
              }
            });
            
            return { ...listing, score };
          });
          
          // Sort by score (descending), then by created_at (descending)
          const sortedListings = scoredListings
            .filter(l => l.score > 0)
            .sort((a, b) => {
              if (b.score !== a.score) return b.score - a.score;
              return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
            })
            .slice(0, 6);
          
          if (sortedListings.length > 0) {
            // Batch fetch saved status for personalized listings
            const listingIds = sortedListings.map(l => l.id);
            const { data: savedData } = await supabase
              .from('saved_listings')
              .select('listing_id')
              .eq('user_id', user.id)
              .in('listing_id', listingIds);
            
            const savedIds = new Set(savedData?.map(s => s.listing_id) || []);
            const listingsWithSavedStatus = sortedListings.map(listing => ({
              ...listing,
              isSaved: savedIds.has(listing.id)
            }));
            
            setRecentListings(listingsWithSavedStatus);
          } else {
            // No matches found, fall back to regular listings
            fetchRecentListings();
          }
        } else {
          fetchRecentListings();
        }
      } else {
        // No personalization data, show regular listings
        fetchRecentListings();
      }
    } catch (error) {
      console.error('Error fetching personalized listings:', error);
      fetchRecentListings();
    }
    
    setIsLoadingListings(false);
  };

  const fetchRecentListings = async () => {
    setIsLoadingListings(true);
    
    const { data, error } = await supabase
      .from('listings')
      .select('id, title, price, images, created_at, user_id')
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(6);
    
    if (data && !error) {
      // Batch fetch saved status for all listings if user is logged in
      if (user && data.length > 0) {
        const listingIds = data.map(l => l.id);
        const { data: savedData } = await supabase
          .from('saved_listings')
          .select('listing_id')
          .eq('user_id', user.id)
          .in('listing_id', listingIds);
        
        const savedIds = new Set(savedData?.map(s => s.listing_id) || []);
        const listingsWithSavedStatus = data.map(listing => ({
          ...listing,
          isSaved: savedIds.has(listing.id)
        }));
        setRecentListings(listingsWithSavedStatus);
      } else {
        setRecentListings(data);
      }
    }
    
    setIsLoadingListings(false);
  };

  // Pull-to-refresh handler for mobile/tablet
  const handleRefresh = useCallback(async () => {
    if (feedMode === "personalized" && user && hasPersonalizationData) {
      await fetchPersonalizedListings();
    } else {
      await fetchRecentListings();
    }
  }, [feedMode, user, hasPersonalizationData]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      // Auto-save search for logged-in users
      if (user) {
        await supabase.from('recent_searches').insert({
          user_id: user.id,
          search_term: searchTerm.trim()
        });
      }
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    }
  };
  const handleSearchSubmit = async (term: string) => {
    if (term.trim()) {
      // Auto-save search for logged-in users
      if (user) {
        await supabase.from('recent_searches').insert({
          user_id: user.id,
          search_term: term.trim()
        });
      }
      navigate(`/search?q=${encodeURIComponent(term)}`);
    }
  };

  // Wrap content with PullToRefresh for mobile/tablet
  const pageContent = (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>HittaDelar - Sveriges marknadsplats för begagnade bildelar</title>
        <meta name="description" content="Köp och sälj begagnade bildelar i Sverige. Hitta reservdelar till alla bilmärken till bra priser. Snabb leverans och säkra köp." />
        <link rel="canonical" href="https://hittadelar.se/" />
        <meta property="og:title" content="HittaDelar - Sveriges marknadsplats för begagnade bildelar" />
        <meta property="og:description" content="Köp och sälj begagnade bildelar i Sverige. Hitta reservdelar till alla bilmärken till bra priser. Snabb leverans och säkra köp." />
        <meta property="og:url" content="https://hittadelar.se/" />
        <meta property="og:type" content="website" />
      </Helmet>


      <main>
      {/* Hero Section */}
        <section className="bg-gradient-to-b from-secondary to-background py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 md:mb-4">Hitta rätt bildelar till bra pris</h1>
            <p className="text-lg sm:text-xl text-muted-foreground mb-6 md:mb-8">
              Sveriges marknadsplats för begagnade bildelar
            </p>
            
            <form onSubmit={handleSearch} className="flex flex-col gap-3">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-2">
                <SearchInput 
                  value={searchTerm} 
                  onChange={setSearchTerm} 
                  onSubmit={handleSearchSubmit} 
                  placeholder="Sök efter bildelar, märke eller modell..." 
                  className="h-12 sm:h-14 text-base sm:text-lg touch-target" 
                  user={user}
                  showPinnedInDropdown={true}
                  allowDirectPin={true}
                />
                <Button type="submit" size="lg" className="h-12 sm:h-14 px-6 sm:px-8 w-full sm:w-auto touch-target">
                  Sök
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Recent Listings Section */}
      {(isLoadingListings || recentListings.length > 0) && (
        <section className="pt-6 pb-8 md:pt-8 md:pb-16 bg-secondary/30 min-h-[400px] md:min-h-[500px]">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center mb-6 md:mb-8 max-w-6xl mx-auto w-full gap-3 md:gap-0 md:relative">
              <h2 className="text-2xl sm:text-3xl font-bold text-center">
                {feedMode === "personalized" ? "Anpassade för dig" : "Senaste annonser"}
              </h2>
              <div className="flex items-center gap-3 md:absolute md:right-0">
                {user && hasPersonalizationData && (
                  <div className="flex items-center gap-2">
                    <Label 
                      htmlFor="personalized-mode" 
                      className={`text-sm cursor-pointer transition-colors ${feedMode === "latest" ? "text-foreground font-medium" : "text-muted-foreground"}`}
                    >
                      Senaste
                    </Label>
                    <Switch
                      id="personalized-mode"
                      checked={feedMode === "personalized"}
                      onCheckedChange={(checked) => setFeedMode(checked ? "personalized" : "latest")}
                    />
                    <Label 
                      htmlFor="personalized-mode" 
                      className={`text-sm cursor-pointer transition-colors ${feedMode === "personalized" ? "text-foreground font-medium" : "text-muted-foreground"}`}
                    >
                      För dig
                    </Label>
                  </div>
                )}
                {/* Mobile column toggle */}
                {isMobile && (
                  <div className="flex items-center border rounded-md">
                    <Button
                      variant={mobileColumns === 1 ? "secondary" : "ghost"}
                      size="icon"
                      className="h-10 w-10 rounded-r-none"
                      onClick={() => setMobileColumns(1)}
                      aria-label="En kolumn"
                    >
                      <Square className="h-4 w-4" />
                    </Button>
                    <Button
                      variant={mobileColumns === 2 ? "secondary" : "ghost"}
                      size="icon"
                      className="h-10 w-10 rounded-l-none border-l"
                      onClick={() => setMobileColumns(2)}
                      aria-label="Två kolumner"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <div className={`grid gap-4 md:gap-6 max-w-6xl mx-auto ${isMobile ? (mobileColumns === 2 ? 'grid-cols-2' : 'grid-cols-1') : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {isLoadingListings ? (
                // Use skeleton component for loading state
                <>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={`skeleton-${index}`} className="space-y-3">
                      <div className="aspect-[4/3] bg-muted rounded-lg animate-pulse" />
                      <div className="space-y-2">
                        <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                        <div className="h-4 bg-muted rounded animate-pulse w-1/2" />
                        <div className="h-5 bg-muted rounded animate-pulse w-1/3" />
                      </div>
                    </div>
                  ))}
                </>
              ) : recentListings.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-muted-foreground text-lg">
                    Inga annonser hittades
                  </p>
                </div>
              ) : (
                recentListings.map((listing, index) => (
                  <Card 
                    key={listing.id} 
                    className="cursor-pointer hover:shadow-lg transition-shadow" 
                    onClick={() => navigate(`/listing/${listing.id}`)}
                  >
                    <div className="aspect-[4/3] bg-secondary overflow-hidden relative">
                      {listing.images[0] ? (
                        <img 
                          src={getMediumImageUrl(listing.images[0])} 
                          alt={`${listing.title} - Begagnad bildel till salu`}
                          loading={index < 3 ? "eager" : "lazy"}
                          fetchPriority={index < 2 ? "high" : undefined}
                          decoding="async"
                          width="400"
                          height="300"
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Car className="h-12 w-12 text-muted-foreground" />
                        </div>
                      )}
                      {isListingNew(listing.created_at) && <NewBadge className="absolute top-2 left-2" />}
                      <div className={`absolute top-2 right-2 ${isMobile && mobileColumns === 2 ? 'scale-75 origin-top-right' : ''}`}>
                        <SaveButton 
                          listingId={listing.id} 
                          userId={user?.id} 
                          size="sm" 
                          variant="outline" 
                          initialIsSaved={listing.isSaved}
                        />
                      </div>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-1 line-clamp-2">{listing.title}</h3>
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-bold text-primary">
                          {listing.price.toLocaleString('sv-SE')} kr
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatPublishedAt(listing.created_at)}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-12 md:py-16 min-h-[292px]">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 md:gap-8">
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Enkelt att hitta</h3>
              <p className="text-muted-foreground">Sök bland begangnade bildelar från hela Sverige</p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Lätt att sälja</h3>
              <p className="text-muted-foreground">
                Lägg upp din annons på några minuter
              </p>
            </div>
            
            <div className="text-center">
              <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <HandCoins className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Behåll 100% av försäljningen</h3>
              <p className="text-muted-foreground">
                Hos oss går hela beloppet direkt till säljaren — inga dolda avgifter eller provisioner.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary text-primary-foreground py-12 md:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3 md:mb-4">
            Redo att börja sälja dina bildelar?
          </h2>
          <p className="text-lg sm:text-xl mb-6 md:mb-8 opacity-90">
            Skapa ett konto idag och nå tusentals köpare
          </p>
          <Button size="lg" variant="secondary" onClick={() => navigate(user ? "/create-listing" : "/auth?tab=signup")} className="touch-target">
            {user ? "Lägg till annons" : "Kom igång nu"}
          </Button>
        </div>
      </section>
      </main>
    </div>
  );

  // Return with or without PullToRefresh based on device
  if (showPullToRefresh) {
    return (
      <PullToRefresh onRefresh={handleRefresh}>
        {pageContent}
      </PullToRefresh>
    );
  }

  return pageContent;
};
export default Index;